import React, {ReactNode, useCallback} from "react";
import {useSelector} from "react-redux";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";

import {IUserUpdatePhoneNumberModalContextValue, UserUpdatePhoneNumberModalContext} from "@pg-mono/user";

import type {IRPStore} from "../../app/rp_reducer";
import {closeFlowVariantB} from "../../auth/state/registration_flow_variantB_slice";
import {validationMessages} from "../../form/utils/validation_messages";
import {addNotification} from "../../notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch} from "../../store/hooks";
import {isRtkqMutationError} from "../../store/utils/is_rtkq_mutation_error";
import {useGetUserProfileQuery} from "../api/get_user_profile";
import {usePatchUserProfileMutation} from "../api/patch_user_profile";

interface IProps {
    children: ReactNode;
}

export const UserUpdatePhoneNumberModalProvider = (props: IProps) => {
    const {children} = props;
    const dispatch = useAppDispatch();
    const {data: userInfo} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();
    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);
    const defaultCountryCode = 48;

    const getUserProfile = useCallback(async () => {
        if (!userInfo) {
            return null;
        }
        return userInfo;
    }, [userInfo]);

    const updateUserProfile = useCallback(
        async (data: {name: string; phone: string}) => {
            const response = await patchUserProfile({
                name: data.name,
                phone: data.phone
            });

            if (isRtkqMutationError(response)) {
                const error = response.error as FetchBaseQueryError & {data?: Record<string, string[]>};
                return {error};
            }

            return {};
        },
        [patchUserProfile]
    );

    const getCountryCodes = useCallback(() => {
        return countryCodes;
    }, [countryCodes]);

    const onClose = useCallback(() => {
        dispatch(closeFlowVariantB());
    }, [dispatch]);

    const onError = useCallback(
        (_error: {message: string}) => {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
        },
        [dispatch]
    );

    const getValidationMessage = useCallback((key: string) => {
        return validationMessages[key as keyof typeof validationMessages] || "";
    }, []);

    const contextValue: IUserUpdatePhoneNumberModalContextValue = {
        getUserProfile,
        updateUserProfile,
        getCountryCodes,
        onClose,
        onError,
        getValidationMessage,
        defaultCountryCode
    };

    return <UserUpdatePhoneNumberModalContext.Provider value={contextValue}>{children}</UserUpdatePhoneNumberModalContext.Provider>;
};
